import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Home } from './modules/pages/Home';
import { Dashboard } from './modules/pages/Dashboard';
import { Login } from './modules/pages/Login';
import { Wizard } from './modules/pages/wizard/Wizard';
import { PublicInvitation } from './modules/pages/PublicInvitation';
import { Features } from './modules/pages/Features';
import { Designs } from './modules/pages/Designs';
import { Pricing } from './modules/pages/Pricing';
import { Company } from './modules/pages/Company';
import { Calendar } from './modules/pages/Calendar';
import { Support } from './modules/pages/Support';
import { SubscriptionPage } from './modules/pages/SubscriptionPage';
import { PartnerProfile } from './modules/pages/PartnerProfile';
import { Settings } from './modules/pages/settings/Settings';
import { FeedbackPage } from './modules/pages/feedback/FeedbackPage';
import { FeedbackDetailPage } from './modules/pages/feedback/FeedbackDetailPage';
import { HelpCenterPage } from './modules/pages/help/HelpCenterPage';
import { HelpArticlePage } from './modules/pages/help/HelpArticlePage';
import { TicketsPage } from './modules/pages/help/TicketsPage';
import { NewTicketPage } from './modules/pages/help/NewTicketPage';
import { TicketDetailPage } from './modules/pages/help/TicketDetailPage';
import { InvitationsPage } from './modules/invitations/InvitationsPage';
import { NotificationsInboxPage } from './modules/pages/notifications/NotificationsInboxPage';
import { Analytics } from './modules/pages/Analytics';
import { DashboardLayout, SiteLayout } from './modules/components/Layout';
import { Header } from './modules/components/Header';
import { SidebarProvider } from './modules/components/sidebar/SidebarContext';
import { ProtectedRoute } from './modules/components/ProtectedRoute';
import { GuestManagementPage } from './modules/guests/GuestManagementPage';
import { RsvpDashboardPage } from './modules/rsvp';
import { BlessingsManagementPage } from './modules/guestbook/BlessingsManagementPage';
import { DigitalPassPage } from './modules/pass/DigitalPassPage';
import { CheckinPage } from './modules/checkin/CheckinPage';
import { GiftExperiencePage } from './modules/gift/GiftExperiencePage';
import { CapsuleGiftDashboardPage } from './modules/gift/CapsuleGiftDashboardPage';
import CircularGalleryDemo from '../components/ui/demo';
import { AnalyticsTracker } from './infrastructure/shared/analytics/AnalyticsTracker';
import { BehaviourTracker } from './infrastructure/shared/analytics/BehaviourTracker';
import { ExperiencePreview } from './modules/pages/ExperiencePreview';
import { TemplateDirectPreviewPage } from './modules/pages/TemplateDirectPreviewPage';
import { VirtualWeddingMVP } from './modules/pages/VirtualWeddingMVP';

import { PolicyPage } from './modules/pages/PolicyPage';

function App() {
  return (
    <BrowserRouter>
      {/* Fires GA4 page_view on every SPA route change */}
      <AnalyticsTracker />
      {/* Click / scroll-depth / section-dwell tracking on public marketing pages */}
      <BehaviourTracker />
      <Routes>
        {/* Public site routes */}
        <Route element={<SidebarProvider><Header /><SiteLayout /></SidebarProvider>}>
          <Route index element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/designs" element={<Designs />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/company" element={<Company />} />
          <Route path="/templates" element={<Navigate to="/designs" replace />} />
          
          {/* Policy Routes */}
          <Route path="/terms" element={<PolicyPage defaultSlug="terms" />} />
          <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
          <Route path="/privacy-policy" element={<PolicyPage defaultSlug="privacy-policy" />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/acceptable-use-policy" element={<PolicyPage defaultSlug="acceptable-use-policy" />} />
          <Route path="/cancellation-refund-policy" element={<PolicyPage defaultSlug="cancellation-refund-policy" />} />
          <Route path="/refund-policy" element={<Navigate to="/cancellation-refund-policy" replace />} />
          <Route path="/shipping-delivery-policy" element={<PolicyPage defaultSlug="shipping-delivery-policy" />} />
          <Route path="/shipping-policy" element={<Navigate to="/shipping-delivery-policy" replace />} />
          <Route path="/delivery-policy" element={<Navigate to="/shipping-delivery-policy" replace />} />
          <Route path="/contact-us" element={<PolicyPage defaultSlug="contact-us" />} />
          <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
          <Route path="/prepaid-credits-policy" element={<Navigate to="/subscription-policy" replace />} />
          <Route path="/cookies-policy" element={<PolicyPage defaultSlug="cookies-policy" />} />
          <Route path="/copyrights-policy" element={<PolicyPage defaultSlug="copyrights-policy" />} />
          <Route path="/content-moderation-policy" element={<PolicyPage defaultSlug="content-moderation-policy" />} />
          <Route path="/disclaimer" element={<PolicyPage defaultSlug="disclaimer" />} />
          <Route path="/security-policy" element={<PolicyPage defaultSlug="security-policy" />} />
          <Route path="/subscription-policy" element={<PolicyPage defaultSlug="subscription-policy" />} />
          <Route path="/support-policy" element={<PolicyPage defaultSlug="support-policy" />} />
          <Route path="/policy/:slug" element={<PolicyPage />} />
        </Route>

        {/* Auth routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Login initialTab="signup" />} />
        <Route path="/i/:slug" element={<PublicInvitation />} />
        {/* Public digital pass — guests open this from their invitation email */}
        <Route path="/pass/:guestCode" element={<DigitalPassPage />} />
        {/* Public Memory Capsule Gift experience */}
        <Route path="/gift/:giftSlug" element={<GiftExperiencePage />} />
        {/* Demo route for 21st Dev Circular Gallery */}
        <Route path="/demo" element={<CircularGalleryDemo />} />
        <Route path="/experience-preview" element={<ExperiencePreview />} />
        {/* Direct template preview route for quick testing and Playwright E2E */}
        <Route path="/preview/:templateId" element={<TemplateDirectPreviewPage />} />
        {import.meta.env.DEV && (
          <Route path="/virtual-wedding" element={<VirtualWeddingMVP />} />
        )}

        {/* Protected routes — redirect to /login if unauthenticated */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="invitations" element={<InvitationsPage />} />
            <Route
              path="invitations/:invitationId/guests"
              element={<GuestManagementPage />}
            />
            <Route
              path="invitations/:invitationId/rsvps"
              element={<RsvpDashboardPage />}
            />
            <Route
              path="invitations/:invitationId/capsule-gift"
              element={<CapsuleGiftDashboardPage />}
            />
            <Route
              path="invitations/:invitationId/blessings"
              element={<BlessingsManagementPage />}
            />
            <Route path="drafts" element={<Navigate to="/dashboard/invitations" replace />} />
            <Route path="calendar" element={<Calendar />} />
            <Route element={<ProtectedRoute permission="VIEW_ANALYTICS" />}>
              <Route path="analytics" element={<Analytics />} />
            </Route>
            <Route path="partner" element={<PartnerProfile />} />
            <Route path="chat" element={<Support />} />
            <Route path="feedback" element={<FeedbackPage />} />
            <Route path="feedback/:id" element={<FeedbackDetailPage />} />
            <Route path="notifications" element={<NotificationsInboxPage />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="settings" element={<Settings />} />
            <Route path="help" element={<HelpCenterPage />} />
            <Route path="help/articles/:slug" element={<HelpArticlePage />} />
            <Route path="help/tickets" element={<TicketsPage />} />
            <Route path="help/tickets/new" element={<NewTicketPage />} />
            <Route path="help/tickets/:id" element={<TicketDetailPage />} />
          </Route>
          <Route path="/wizard" element={<Wizard />} />
          {/* Gate-staff check-in app — requires the CHECKIN_VERIFY permission */}
          <Route element={<ProtectedRoute permission="CHECKIN_VERIFY" />}>
            <Route path="/checkin" element={<CheckinPage />} />
          </Route>
        </Route>

        <Route path="/share" element={<Navigate to="/" replace />} />
        <Route path="/export" element={<Navigate to="/" replace />} />
        <Route path="/pdf" element={<Navigate to="/" replace />} />
        <Route path="/gif" element={<Navigate to="/" replace />} />
        <Route path="/download" element={<Navigate to="/" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
